import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { StatusChip, batteryStatusTone } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, QrCode, OctagonPause, OctagonX, Printer } from "lucide-react";

export default function Batteries() {
  const [searchParams] = useSearchParams();
  const [batteries, setBatteries] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") || "all");
  const [addOpen, setAddOpen] = useState(false);
  const [serial, setSerial] = useState("");
  const [holdTarget, setHoldTarget] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [qrTarget, setQrTarget] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/batteries", { params }).then((r) => { setBatteries(r.data); setSelected([]); }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/batteries", { serial_number: serial });
      toast.success("Battery added");
      setAddOpen(false); setSerial("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const hold = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/batteries/${holdTarget.battery_id}/hold`, { reason: holdReason });
      toast.success(`${holdTarget.battery_id} placed on hold`);
      setHoldTarget(null); setHoldReason("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const release = async (b) => {
    try {
      await api.post(`/batteries/${b.battery_id}/release`);
      toast.success(`${b.battery_id} released to Available`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-5" data-testid="batteries-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Batteries</h1>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-card border-border" data-testid="battery-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Available">Available</SelectItem>
              <SelectItem value="In Use">In Use</SelectItem>
              <SelectItem value="On Hold">On Hold</SelectItem>
              <SelectItem value="Faulty">Faulty</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="print-qr-btn" disabled={selected.length === 0}
            onClick={() => setPrintItems(batteries.filter((b) => selected.includes(b.battery_id)))}
            className="border-slate-300 font-semibold">
            <Printer className="h-4 w-4 mr-1.5" /> Print QR{selected.length > 0 ? ` (${selected.length})` : ""}
          </Button>
          <Button data-testid="add-battery-btn" onClick={() => setAddOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Battery
          </Button>
        </div>
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3 w-8">
              <input type="checkbox" data-testid="select-all-checkbox" className="h-4 w-4 accent-teal-600"
                checked={batteries.length > 0 && selected.length === batteries.length}
                onChange={(e) => setSelected(e.target.checked ? batteries.map((b) => b.battery_id) : [])} />
            </th>
            <th className="p-3">Battery ID</th><th className="p-3">Serial Number</th><th className="p-3">Status</th>
            <th className="p-3">Rider</th><th className="p-3">Hold Reason</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {batteries.map((b) => (
              <tr key={b.battery_id} className="border-b border-border/50 hover:bg-slate-50" data-testid={`battery-row-${b.battery_id}`}>
                <td className="p-3">
                  <input type="checkbox" data-testid={`select-${b.battery_id}`} className="h-4 w-4 accent-teal-600"
                    checked={selected.includes(b.battery_id)}
                    onChange={(e) => setSelected(e.target.checked
                      ? [...selected, b.battery_id]
                      : selected.filter((x) => x !== b.battery_id))} />
                </td>
                <td className="p-3 font-mono font-medium">{b.battery_id}</td>
                <td className="p-3 font-mono text-xs">{b.serial_number}</td>
                <td className="p-3"><StatusChip label={b.status} tone={batteryStatusTone(b.status)} testid={`battery-status-${b.battery_id}`} /></td>
                <td className="p-3 font-mono text-xs">{b.rider_id || "—"}</td>
                <td className="p-3 text-xs text-slate-400 max-w-[200px] truncate">{b.hold_reason || "—"}</td>
                <td className="p-3">
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                      data-testid={`qr-${b.battery_id}`} onClick={() => setQrTarget(b)}>
                      <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                    </Button>
                    {b.status !== "On Hold" ? (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-purple-800 text-purple-300"
                        data-testid={`hold-${b.battery_id}`} onClick={() => setHoldTarget(b)}>
                        <OctagonPause className="h-3.5 w-3.5 mr-1" /> Hold
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-800 text-emerald-300"
                        data-testid={`release-${b.battery_id}`} onClick={() => release(b)}>
                        <OctagonX className="h-3.5 w-3.5 mr-1" /> Release
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {batteries.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="batteries-empty">No batteries found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border" data-testid="add-battery-modal">
          <DialogHeader><DialogTitle className="font-heading">Add Battery</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Serial Number (from barcode/QR) *</label>
              <Input data-testid="battery-serial-input" required value={serial} onChange={(e) => setSerial(e.target.value)}
                className="bg-slate-50 border-border font-mono" placeholder="e.g. YGB-2026-000123" />
            </div>
            <Button type="submit" disabled={busy} data-testid="add-battery-submit"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
              {busy ? "Adding…" : "Add Battery"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!holdTarget} onOpenChange={() => setHoldTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="hold-battery-modal">
          <DialogHeader><DialogTitle className="font-heading">Hold {holdTarget?.battery_id}</DialogTitle></DialogHeader>
          <form onSubmit={hold} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Hold Reason *</label>
              <Textarea data-testid="hold-reason-input" required value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                rows={3} className="bg-slate-50 border-border" placeholder="e.g. Suspected cell damage, awaiting inspection" />
            </div>
            <Button type="submit" disabled={busy || !holdReason.trim()} data-testid="hold-battery-modal-submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold">
              {busy ? "Holding…" : "Place On Hold"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrTarget} onOpenChange={() => setQrTarget(null)}>
        <DialogContent className="bg-card border-border sm:max-w-sm" data-testid="battery-qr-modal">
          <DialogHeader><DialogTitle className="font-heading">{qrTarget?.battery_id} QR Code</DialogTitle></DialogHeader>
          {qrTarget && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="bg-white p-4 rounded-xl">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/batteries/${qrTarget.battery_id}/qr`}
                  alt={`QR for ${qrTarget.serial_number}`} className="h-52 w-52" data-testid="battery-qr-image" />
              </div>
              <div className="font-mono text-sm text-slate-700">{qrTarget.serial_number}</div>
              <p className="text-xs text-slate-500 text-center">Print and stick this QR on the battery for scanning.</p>
              <Button variant="outline" data-testid="print-single-qr" className="border-slate-300 font-semibold"
                onClick={() => { setPrintItems([qrTarget]); setQrTarget(null); }}>
                <Printer className="h-4 w-4 mr-1.5" /> Print Label
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!printItems} onOpenChange={() => setPrintItems(null)}>
        <DialogContent className="bg-white border-slate-200 sm:max-w-3xl" data-testid="qr-print-sheet">
          <DialogHeader>
            <DialogTitle className="font-heading">Print QR Labels ({printItems?.length || 0})</DialogTitle>
          </DialogHeader>
          <div className="qr-print-area grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto p-1">
            {printItems?.map((b) => (
              <div key={b.battery_id} data-testid={`print-label-${b.battery_id}`}
                className="border border-slate-300 rounded-xl p-3 flex flex-col items-center gap-1.5 break-inside-avoid">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/batteries/${b.battery_id}/qr`}
                  alt={`QR ${b.serial_number}`} className="h-32 w-32" />
                <div className="font-mono font-bold text-sm">{b.battery_id}</div>
                <div className="font-mono text-[10px] text-slate-500 break-all text-center">{b.serial_number}</div>
              </div>
            ))}
          </div>
          <Button data-testid="do-print-btn" onClick={() => window.print()}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold">
            <Printer className="h-4 w-4 mr-1.5" /> Print
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
